import type {
  AriaAttributes,
  AriaRole,
  ComponentType,
  KeyboardEvent,
} from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import { ColorKey } from 'components/utils/styles/types';

import { Colors } from '../utils/styles/ui';

import { LazyPhosphorIcon } from './LazyPhosphorIcon';
import * as CustomIcons from './customIcons';
import { ICON_SIZES } from './constants/icons';
import { PHOSPHOR_ICONS } from './constants/iconRegistry';
import * as styled from './styles/icon';

import type {
  IconSize,
  IconWeight,
  Color,
  IconName,
  CustomIconKeys,
} from './types';

type IconProps = {
  'aria-hidden'?: AriaAttributes['aria-hidden'];
  alt?: string;
  focusable?: boolean;
  xmlns?: string;
  icon?: IconName | 'DotsThreeOutlineVerticalFill';
  size?: IconSize | number;
  color?: Color | 'currentColor';
  weight?: IconWeight;
  mirrored?: boolean;
  marginLeft?: string | number | boolean;
  label?: string;
  onClick?: (event: KeyboardEvent) => void;
  role?: AriaRole;
};

type IconWrappedProps = {
  className?: string;
  color: ColorKey;
  size: number | IconSize;
  shape?: 'circle' | 'square';
};

type IconCircleProps = {
  className?: string;
  color: ColorKey;
  shadow: ColorKey;
  icon?: IconName;
};

type IconCounterProps = {
  className: string;
  color: ColorKey;
  counter: number;
};

const computeSize = (size: number | IconSize) =>
  (typeof size === 'number' ? size : ICON_SIZES[size]) * 10;

/**
 * "#" in URLs starts a fragment identifier.
 * So, in order to url works, it is needed to escape "#" (%23) for colors
 */

export const toStaticMarkup = (props: IconProps) => {
  const phosphorIconName = props.icon?.replace(/Fill|Mirror$/, '');
  const isRegistered = Boolean(
    PHOSPHOR_ICONS[phosphorIconName as keyof typeof PHOSPHOR_ICONS] ||
      CustomIcons[props.icon as CustomIconKeys]
  );

  if (!isRegistered) {
    throw new Error(
      `Icon "${props.icon}" must be registered before rendering to static markup`
    );
  }

  return renderToStaticMarkup(
    <Icon xmlns="http://www.w3.org/2000/svg" {...props} />
  ).replace(/#/g, '%23');
};

const reportIconIssue = (message: string) => {
  if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
    window.console.error(
      `>>>>>>> ${message}, path: ${window.location.pathname}`
    );
  }
};

const MissingIcon = PHOSPHOR_ICONS.ImageSquare as ComponentType<IconProps>;

export const Icon = function Icon({
  icon,
  size = 'normal',
  weight = 'regular',
  color = Colors.text,
  label,
  ...props
}: IconProps) {
  const phosphorIconName = icon?.replace(/Fill|Mirror$/, '');
  const customIcon = CustomIcons[icon as CustomIconKeys];
  const iconProps = {
    'aria-hidden': label || props.onClick ? undefined : true,
    role: label ? ('img' as const) : undefined,
    focusable: false,
    alt: label,
    ...props,
    mirrored: icon?.includes('Mirror'),
    weight: icon?.includes('Fill') ? ('fill' as IconWeight) : weight,
    color: Colors[color as ColorKey] || color,
    size: computeSize(size),
  };

  const IconComponent = (PHOSPHOR_ICONS[
    phosphorIconName as keyof typeof PHOSPHOR_ICONS
  ] || customIcon) as ComponentType<IconProps>;

  if (IconComponent) {
    if (label && IconComponent === customIcon) {
      if (typeof window !== 'undefined') {
        window.console.error(
          `>>>>>>> Icon label="${label}" has no effect on custom icon "${icon}" — custom icons do not forward accessibility props yet`
        );
      }
    }

    return <IconComponent {...iconProps} />;
  }

  if (icon) {
    reportIconIssue(`Icon "${icon}" is not registered in iconRegistry.ts`);

    return (
      <LazyPhosphorIcon
        icon={icon}
        phosphorIconName={phosphorIconName as string}
        {...iconProps}
      />
    );
  }

  reportIconIssue(`Missing Icon: ${icon}`);
  return <MissingIcon {...iconProps} />;
};

export const IconWrapped = function IconWrapped({
  className,
  color,
  size,
  shape = 'circle',
  ...props
}: IconWrappedProps) {
  const computedSize = computeSize(size) + 2;
  const ShapeWrapper = shape === 'circle' ? styled.Circle : styled.Square;

  return (
    <ShapeWrapper className={className} color={color} size={computedSize}>
      <Icon color="white" size={size} {...props} />
    </ShapeWrapper>
  );
};

export const IconCircle = function IconCircle({
  className,
  color,
  shadow,
  ...props
}: IconCircleProps) {
  return (
    <styled.CircleWrapper className={className} color={color} shadow={shadow}>
      <Icon {...props} size="x2small" color={color} />
    </styled.CircleWrapper>
  );
};

export const IconCounter = function IconCounter({
  className,
  color,
  counter,
  ...props
}: IconCounterProps) {
  return (
    <styled.CounterWrapper className={className}>
      <styled.IconCounter color={color} digits={counter.toString().length}>
        {counter}
      </styled.IconCounter>
      <Icon {...props} color={color} />
    </styled.CounterWrapper>
  );
};
